import DOMPurify, { Config } from 'dompurify';

/**
 * Default configuration for DOMPurify
 * Allows safe HTML tags and attributes
 */
const DEFAULT_CONFIG: Config = {
  ALLOWED_TAGS: [
    'b',
    'i',
    'em',
    'strong',
    'a',
    'p',
    'br',
    'ul',
    'ol',
    'li',
    'span',
    'div',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'code',
    'pre',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id', 'style'],
  ALLOW_DATA_ATTR: false,
  ADD_ATTR: ['target'],
};

/**
 * Sanitize HTML string to prevent XSS attacks
 */
export const sanitizeHTML = (
  html: string | null | undefined,
  options: Config = {},
): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  const config = { ...DEFAULT_CONFIG, ...options };
  return DOMPurify.sanitize(html, config);
};

/**
 * Sanitize HTML and return object for dangerouslySetInnerHTML
 */
export const createSanitizedMarkup = (
  html: string | null | undefined,
  options: Config = {},
): { __html: string } => {
  return {
    __html: sanitizeHTML(html, options),
  };
};

/**
 * Strict sanitization - only allows basic text formatting
 * Use for user-generated content
 */
export const sanitizeUserContent = (
  html: string | null | undefined,
): string => {
  return sanitizeHTML(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p'],
    ALLOWED_ATTR: [],
  });
};

/**
 * Sanitize HTML for rich text content (like blog posts)
 * Allows more tags but still prevents XSS
 */
export const sanitizeRichContent = (
  html: string | null | undefined,
): string => {
  return sanitizeHTML(html, {
    ALLOWED_TAGS: [
      ...(DEFAULT_CONFIG.ALLOWED_TAGS as string[]),
      'img',
      'video',
      'iframe',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
    ],
    ALLOWED_ATTR: [
      ...(DEFAULT_CONFIG.ALLOWED_ATTR as string[]),
      'src',
      'alt',
      'width',
      'height',
      'frameborder',
      'allowfullscreen',
    ],
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
  });
};

/**
 * Remove entity ID references from text
 * Removes patterns like: (campaign: `68ff621c3c8f6fd582e21b68`) or (auth: `6993ae94982daee491b025be`)
 */
export const stripEntityReferences = (
  text: string | null | undefined,
): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Pattern: optional space + ( + word + : + optional space + backtick + alphanumeric id + backtick + )
  return text.replace(/\s?\([a-zA-Z_]+:\s*`[a-zA-Z0-9]+`\)/g, '');
};

export default sanitizeHTML;
